import { useState } from "react";
import { LuChevronDown, LuCircleCheck } from "react-icons/lu";

import Picker from "../components/Picker";
import {
  COUNTRIES,
  DEFAULT_COUNTRY,
  PREFERRED_COUNTRIES,
  countryName,
  countryOf,
  examplePhone,
  flagOf,
  formatNational,
  parseTyped,
  phoneProblem,
  searchCountries,
} from "../lib/phone";

const ALLOWED = /[^\d\s()+-]/g;

const toOption = (c, group) => ({
  value: c.iso,
  label: c.name,
  hint: `+${c.code}`,
  icon: flagOf(c.iso),
  group,
});

// Common countries first, then everyone; a search ranks across all of them.
const COUNTRY_OPTIONS = [
  ...PREFERRED_COUNTRIES.map((iso) => toOption(countryOf(iso), "Common")),
  ...COUNTRIES.filter((c) => !PREFERRED_COUNTRIES.includes(c.iso)).map((c) =>
    toOption(c, "All countries"),
  ),
];

const filterCountries = (options, query) =>
  query.trim() ? searchCountries(query).map((c) => toOption(c)) : options;

// Country code button plus the number. The value is { country, number }:
// the ISO country ("AE") and the number as typed in national form. Pasting
// or typing a full "+44 ..." number switches the country by itself.
export default function PhoneInput({ id, value, onChange, onBlur, invalid, describedBy }) {
  const [open, setOpen] = useState(false);
  const country = value?.country || DEFAULT_COUNTRY;
  const number = value?.number || "";
  const { code } = countryOf(country);
  const valid = Boolean(number) && !phoneProblem(number, country);

  const setNumber = (raw) => {
    const typed = raw.replace(ALLOWED, "");
    const parsed = /^(\+|00)/.test(typed.trim()) ? parseTyped(typed, country) : null;
    if (parsed?.country) {
      onChange({ country: parsed.country, number: parsed.formatNational() });
      return;
    }
    onChange({ country, number: typed });
  };

  const pick = (iso) => {
    setOpen(false);
    onChange({ country: iso, number });
    document.getElementById(id)?.focus();
  };

  return (
    <div>
      <div className="cr-phone" data-invalid={invalid}>
        <button
          type="button"
          className="cr-phone-country"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={`Country code: ${countryName(country)} +${code}. Change`}
          onClick={() => setOpen((o) => !o)}
        >
          <span className="cr-flag" aria-hidden="true">
            {flagOf(country)}
          </span>
          +{code}
          <LuChevronDown aria-hidden="true" />
        </button>
        <input
          id={id}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          placeholder={examplePhone(country)}
          value={number}
          aria-invalid={invalid}
          aria-describedby={describedBy}
          onChange={(e) => setNumber(e.target.value)}
          onBlur={() => {
            if (valid) onChange({ country, number: formatNational(number, country) });
            onBlur?.();
          }}
        />
        {open && (
          <Picker
            title="Country code"
            options={COUNTRY_OPTIONS}
            value={country}
            searchable
            searchPlaceholder="Search country or code"
            filter={filterCountries}
            emptyText="No country matches"
            onPick={pick}
            onClose={() => {
              setOpen(false);
              document.getElementById(id)?.focus();
            }}
          />
        )}
      </div>
      {valid && !invalid && (
        <p className="cr-phone-ok">
          <LuCircleCheck aria-hidden="true" /> Valid {countryName(country)} mobile number
        </p>
      )}
    </div>
  );
}
